import type { GroupId } from "../workspace/workspace.types";
import type { GroupRegistryController } from "./GroupRegistry.context";
import { reportGroupRegistryError } from "./groupRegistry.repository";

export interface TemplateGroupSelection {
  groupId: GroupId;
  /**
   * True only when a new letter was allocated for this workspace. The caller
   * must release it (`deleteGroup`) if the workspace commit fails, otherwise a
   * failed creation leaks the letter and pushes later templates onto the
   * saturated fallback group.
   */
  created: boolean;
}

/**
 * Picks the group every widget of a new template workspace joins: a freshly
 * allocated letter, or the last existing one once all 26 letters are taken
 * (`createGroup` resolves `undefined` only in that case).
 *
 * Resolves `undefined` when the registry is unavailable, the write fails, or no
 * group exists to fall back to, so a registry problem leaves the workspace
 * unlinked instead of uncreated.
 */
export const resolveTemplateGroupId = async (
  registry: GroupRegistryController | null,
): Promise<TemplateGroupSelection | undefined> => {
  if (!registry) {
    return undefined;
  }
  try {
    const created = await registry.createGroup();
    if (created !== undefined) {
      return { groupId: created, created: true };
    }
    const fallback = registry.getSnapshot().groups.at(-1)?.id;
    return fallback === undefined
      ? undefined
      : { groupId: fallback, created: false };
  } catch (error) {
    reportGroupRegistryError(error);
    return undefined;
  }
};
